import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { NotificationToast } from "../../../../../../../../../../components/Generic/Toast/NotificationToast";
import { MemberCertification } from "../../../../../../../../../../models/members/member.certification";
import { Filters, Searches } from "../../../../../../../../../../models/shipeezi.table";
import MemberCertificationService from "../../../../../../../../../../services/certifications.service";
import { CertificationKeys, MemberKeys } from "../../../../../../../../../../util/queries";

export const useCertificationTabActions = (memberId?: number) => {
  const { t: translate } = useTranslation();
  const queryClient = useQueryClient();

  const getCertifications = (
    page: number,
    pageSize: number,
    search: Searches,
    filters: Filters
  ) => {
    return useQuery({
      queryKey: CertificationKeys.getAll([memberId, page, pageSize, filters, search]),
      queryFn: async () => {
        const response = await MemberCertificationService.getAll({
          page,
          pageSize,
          memberId,
          searchByOr: search?.length ? JSON.stringify(search) : "",
          include: JSON.stringify([
            {
              association: "generalFile",
            },
            {
              association: "member",
              attribute: ["id", "fullName"],
            },
          ]),
          ...filters,
        });

        if (response?.data && Array.isArray(response.data)) {
          return response.data;
        }

        return [];
      },
      enabled: !!memberId,
      refetchOnWindowFocus: false,
    });
  };

  const getCertificationsCount = (filters: Filters, search: Searches) => {
    return useQuery({
      queryKey: CertificationKeys.count([filters, search]),
      queryFn: async () => {
        const response = await MemberCertificationService.count({
          memberId,
          searchByOr: search?.length ? JSON.stringify(search) : "",
          include: JSON.stringify([
            {
              association: "generalFile",
            },
            {
              association: "member",
              attribute: ["id", "fullName"],
            },
          ]),
          ...filters,
        });

        if (response && response.data) return response.data;

        return 0;
      },
      enabled: !!memberId,
      refetchOnWindowFocus: false,
    });
  };

  const useManageCertification = (onSuccess: () => void) => {
    return useMutation({
      mutationKey: CertificationKeys.create(),
      mutationFn: async (certification: MemberCertification) => {
        if (certification?.id) {
          return MemberCertificationService.update(certification.id, certification);
        }

        return MemberCertificationService.create(certification);
      },
      onSuccess: () => {
        NotificationToast.success(translate("operationPerformedSuccessfully"));
        queryClient.invalidateQueries(CertificationKeys.getAll([]));
        onSuccess();
      },
    });
  };

  const useApproveCertification = (onSuccess: () => void) => {
    return useMutation({
      mutationKey: CertificationKeys.approve(),
      mutationFn: async ({ id }: { id: number }) =>
        MemberCertificationService.approveCertifaction(id),
      onSuccess: () => {
        NotificationToast.success(translate("operationPerformedSuccessfully"));
        queryClient.invalidateQueries(CertificationKeys.getAll([]));
        queryClient.invalidateQueries({
          queryKey: MemberKeys.getAll(["despatch-more"]),
          refetchType: "all",
        });
        onSuccess();
      },
    });
  };

  return {
    getCertifications,
    getCertificationsCount,
    useManageCertification,
    useApproveCertification,
  };
};
